'use client';

import { useCallback, useMemo, useContext } from 'react';

import { RealEstateDict, useLocale } from '@monoimin/intl';

import { useParamsUpdater } from '@monoimin/hooks';

import {
  collectParamsFilterValues,
  updateFiltersState,
} from '../../utils/filters';

import { SelectWithSearch } from '../SelectWithSearch';

import { FiltersContext } from './FiltersProvider';

import type {
  SelectItem,
  SelectFilterConfig,
  EstateUnitFilterKeys,
} from '../../types';
import type { EnOrRuLocale } from '@monoimin/sb-types';

export type OnChangeFunction<T extends unknown[] = []> = (
  key: EstateUnitFilterKeys,
  value: SelectItem | Array<SelectItem>,
  ...args: T
) => void;

export type SelectFilterProps = SelectFilterConfig & {
  newPathname?: string;
  onChange?: OnChangeFunction;
  realEstateDict: RealEstateDict;
};

const sortItemByTitle = <T extends { title: string | undefined }>(
  a: T,
  b: T
) => {
  const aTitle = String(a.title).toLowerCase();

  const bTitle = String(b.title).toLowerCase();

  if (aTitle < bTitle) {
    return -1;
  }

  if (aTitle > bTitle) {
    return 1;
  }

  return 0;
};

export const SelectFilter = ({
  placeholderDictionaryKey,
  withSearch,
  filterKey,
  filterItems,
  variant,
  searchPlaceholderDictionaryKey,
  newPathname,
  onChange,
  realEstateDict,
}: SelectFilterProps) => {
  const [filtersState] = useContext(FiltersContext);

  const locale = useLocale<EnOrRuLocale>();

  const data = filterItems[locale];

  const { onChange: onParamsChange } = useParamsUpdater();

  const sortedData = useMemo(() => {
    return [...data].sort(sortItemByTitle);
  }, [data]);

  const currentStateValue = useMemo(() => {
    return filtersState?.[filterKey] ?? [];
  }, [filtersState, filterKey]);

  const value = useMemo(() => {
    return collectParamsFilterValues(sortedData, currentStateValue);
  }, [sortedData, currentStateValue]);

  const onFilterChange = useCallback<OnChangeFunction>(
    (key, value) => {
      const newFiltesState = updateFiltersState({
        key: key,
        selectedData: value,
        filtersState: filtersState,
      });

      onParamsChange(newFiltesState, newPathname);
    },
    [onParamsChange, filtersState, newPathname]
  );

  // Вынести выше
  const placeholder = realEstateDict[placeholderDictionaryKey];

  // Вынести выше
  const searchPlaceholder = searchPlaceholderDictionaryKey
    ? realEstateDict[searchPlaceholderDictionaryKey]
    : '';

  return (
    <SelectWithSearch
      data={sortedData}
      realEstateDict={realEstateDict}
      placeholder={placeholder as string}
      value={value}
      onChange={(value) => {
        if (typeof onChange === 'function') {
          onChange(filterKey, value);
        } else {
          onFilterChange(filterKey, value);
        }
      }}
      searchConfig={{
        disabled: !withSearch,
        placeholder: searchPlaceholder as string,
      }}
      variant={variant}
    />
  );
};
